import { parseSlideLayoutJson } from '../types/programContentSlideCanvas';
import { supabase } from '../supabaseClient';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  loadProgramSlideRegisteredLayouts,
  saveProgramSlideRegisteredLayouts,
  type ProgramSlideRegisteredLayout,
} from '../utils/programSlideRegisteredLayouts';
import {
  normalizeProgramSlideRegistryName,
  PROGRAM_SLIDE_REGISTERED_LAYOUT_MAX_COUNT,
  programSlideRegistryFallbackName,
} from '../utils/programSlideRegistryLimits';
import {
  clearRegistryScopeRows,
  createRegistryItemId,
  dedupeRegistryItemIds,
  formatRegistryScopeSaveError,
  insertRegistryScopeRowsWithRetry,
  isRegistryItemId,
} from '../utils/programSlideRegisteredScopeSave';

const REGISTRY_SCOPE_TABLE = 'workspace_slide_registered_layouts' as const;

const SELECT_COLUMNS = 'id, workspace_id, name, layout_json, order_index, created_at, updated_at';

interface WorkspaceSlideRegisteredLayoutRow {
  id: string;
  workspace_id: string;
  name: string;
  layout_json: unknown;
  order_index: number;
  created_at: string;
  updated_at: string;
}

function normalizeName(raw: unknown, fallback: string): string {
  return normalizeProgramSlideRegistryName(raw, fallback);
}

function rowToLayout(
  row: WorkspaceSlideRegisteredLayoutRow,
  idx: number
): ProgramSlideRegisteredLayout {
  return {
    id: row.id,
    name: normalizeName(row.name, programSlideRegistryFallbackName(idx)),
    layout: parseSlideLayoutJson(row.layout_json),
    order:
      Number.isFinite(Number(row.order_index)) && Number(row.order_index) >= 0
        ? Number(row.order_index)
        : idx,
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
}

function sortLayouts(layouts: ProgramSlideRegisteredLayout[]): ProgramSlideRegisteredLayout[] {
  return [...layouts].sort((a, b) => a.order - b.order);
}

/** id:updatedAt(ms) 조합의 head 시그니처. layout_json 본문 없이 캐시 신선도만 검증할 때 사용. */
function buildRegisteredLayoutsHeadVersion(layouts: Array<{ id: string; updatedAt: number }>): string {
  const parts = layouts.map((l) => `${l.id}:${l.updatedAt}`).sort();
  return `${parts.length}|${parts.join(',')}`;
}

async function fetchProgramSlideRegisteredLayoutsHeadVersion(workspaceId: string): Promise<string> {
  const { data, error } = await supabase
    .from('workspace_slide_registered_layouts')
    .select('id, updated_at')
    .eq('workspace_id', workspaceId);
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: String(row.id),
      updatedAt: row.updated_at ? new Date(String(row.updated_at)).getTime() : 0,
    };
  });
  return buildRegisteredLayoutsHeadVersion(rows);
}

async function fetchProgramSlideRegisteredLayoutsFromDb(
  workspaceId: string
): Promise<ProgramSlideRegisteredLayout[]> {
  const { data, error } = await supabase
    .from('workspace_slide_registered_layouts')
    .select(SELECT_COLUMNS)
    .eq('workspace_id', workspaceId)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as WorkspaceSlideRegisteredLayoutRow[];
  return sortLayouts(rows.map((row, idx) => rowToLayout(row, idx)));
}

/**
 * `forceRefresh` 없으면 LS 캐시가 있을 때 `id/updated_at` head만 먼저 비교하고, 동일하면
 * layout_json 본문 재조회 없이 캐시를 재사용한다. head 조회 실패(오프라인 등) 시에는 캐시를 그대로 반환한다.
 */
export async function getProgramSlideRegisteredLayouts(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramSlideRegisteredLayout[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  const persistFromDb = (rows: ProgramSlideRegisteredLayout[]) => {
    // get 경로의 LS 반영은 이벤트 없이 — 리스너가 다시 forceRefresh 하는 루프 방지
    saveProgramSlideRegisteredLayouts(sid, rows, { emitEvent: false });
  };

  if (!options?.forceRefresh) {
    const cached = loadProgramSlideRegisteredLayouts(sid);
    if (cached.length > 0) {
      const cachedVersion = buildRegisteredLayoutsHeadVersion(cached);
      let headVersion: string | null = null;
      try {
        headVersion = await fetchProgramSlideRegisteredLayoutsHeadVersion(sid);
      } catch {
        headVersion = null;
      }
      if (headVersion === null) {
        return cached;
      }
      if (headVersion === cachedVersion) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('슬라이드레이아웃등록·LS(head일치)');
        }
        return cached;
      }
      // head 불일치 → 아래에서 본문 재조회
    }
  }

  const fromDb = await fetchProgramSlideRegisteredLayoutsFromDb(sid);
  if (fromDb.length > 0) {
    persistFromDb(fromDb);
    return fromDb;
  }

  const legacyLocal = loadProgramSlideRegisteredLayouts(sid);
  if (legacyLocal.length > 0) {
    return legacyLocal;
  }

  persistFromDb([]);
  return [];
}

function buildRegisteredLayoutPayload(
  sid: string,
  normalized: Array<{
    id: string;
    name: string;
    layout: ProgramSlideRegisteredLayout['layout'];
    order: number;
  }>,
  now: string
) {
  return normalized.map((item) => ({
    id: item.id,
    workspace_id: sid,
    name: item.name,
    layout_json: item.layout,
    order_index: item.order,
    created_at: now,
    updated_at: now,
  }));
}

type NormalizedRegisteredLayout = {
  id: string;
  name: string;
  layout: ProgramSlideRegisteredLayout['layout'];
  order: number;
};

async function insertRegisteredLayouts(
  sid: string,
  normalized: NormalizedRegisteredLayout[],
  now: string
): Promise<WorkspaceSlideRegisteredLayoutRow[]> {
  const data = await insertRegistryScopeRowsWithRetry(
    '레이아웃',
    REGISTRY_SCOPE_TABLE,
    sid,
    normalized,
    async (rows) => {
      const payload = buildRegisteredLayoutPayload(sid, rows, now);
      return supabase
        .from(REGISTRY_SCOPE_TABLE)
        .insert(payload)
        .select(SELECT_COLUMNS)
        .order('order_index', { ascending: true });
    }
  );
  return (data ?? []) as unknown as WorkspaceSlideRegisteredLayoutRow[];
}

export async function replaceProgramSlideRegisteredLayouts(
  workspaceId: string,
  layouts: ProgramSlideRegisteredLayout[]
): Promise<ProgramSlideRegisteredLayout[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  const now = new Date().toISOString();
  const sliced = layouts.slice(0, PROGRAM_SLIDE_REGISTERED_LAYOUT_MAX_COUNT);
  const dedupedIds = dedupeRegistryItemIds(
    sliced.map((item) => String(item.id ?? '').trim())
  );
  const normalized = sliced
    .map((item, idx) => ({
      id: dedupedIds[idx] ?? createRegistryItemId(),
      name: normalizeName(item.name, programSlideRegistryFallbackName(idx)),
      layout: item.layout,
      order: idx,
      updatedAt: Number.isFinite(item.updatedAt) ? item.updatedAt : Date.now(),
    }))
    .filter((item) => isRegistryItemId(item.id));

  await clearRegistryScopeRows(REGISTRY_SCOPE_TABLE, sid, '레이아웃');

  if (!normalized.length) {
    saveProgramSlideRegisteredLayouts(sid, []);
    return [];
  }

  const rows = await insertRegisteredLayouts(sid, normalized, now);
  const saved = sortLayouts(rows.map((row, idx) => rowToLayout(row, idx)));
  saveProgramSlideRegisteredLayouts(sid, saved);
  return saved;
}
